import { type AnyRouter } from "@tanstack/react-router";
import { type Tenant } from "@/app/Organisations/Utils/tenant";

/** Params owned by the scope prefix; anything else identifies a record. */
const SCOPE_PARAM_KEYS = new Set(["tenant", "orgId", "supplierId"]);

export type ScopeChange = {
  tenant: Tenant;
  orgId: string;
  supplierId: string | null;
};

/**
 * Destination pathname for an org/supplier scope change, so the switcher can
 * navigate via the router instead of reloading the page.
 *
 * Record-detail screens — those carrying a record id param, e.g.
 * `/relocations/$relocationId` (and its tabs) — drop back to their parent list
 * (`/relocations`), since the record is unlikely to be visible under the new
 * scope. Switching *to* the admin tenant keeps the current screen (admin sees
 * everything), and list screens stay put.
 */
export function buildScopeChangePath(
  router: AnyRouter,
  next: ScopeChange,
): string {
  const { matches, location } = router.state;

  const scopeParams = (matches.find(
    (match) => "orgId" in (match.params as Record<string, string>),
  )?.params ?? {}) as {
    tenant?: string;
    orgId?: string;
    supplierId?: string;
  };

  const currentPrefix =
    `/${scopeParams.tenant}/${scopeParams.orgId}/supplier` +
    (scopeParams.supplierId ? `/${scopeParams.supplierId}` : "");

  const nextPrefix =
    `/${next.tenant}/${next.orgId}/supplier` +
    (next.supplierId ? `/${next.supplierId}` : "");

  const suffix = location.pathname.startsWith(currentPrefix)
    ? location.pathname.slice(currentPrefix.length)
    : "";

  // First match that introduces a record id (a param outside the scope set).
  const recordMatch = matches.find((match) =>
    Object.keys(match.params as Record<string, string>).some(
      (key) => !SCOPE_PARAM_KEYS.has(key),
    ),
  );

  // Keep the screen when not on a record, or when switching to admin.
  if (!recordMatch || next.tenant === "admin") {
    return `${nextPrefix}${suffix}`;
  }

  // Drop to the record's parent list: its pathname minus the record id segment.
  const recordSuffix = recordMatch.pathname.startsWith(currentPrefix)
    ? recordMatch.pathname.slice(currentPrefix.length)
    : suffix;
  const listSuffix = recordSuffix.slice(0, recordSuffix.lastIndexOf("/"));

  return `${nextPrefix}${listSuffix}`;
}
